"use client";
import { useState, useRef } from "react";
import { toKebabCase } from "@utils/stringFormatters";
import { getImageRows } from "./utils";
import { colorMediaService } from "@services/colorMediaService";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getSelectedCityClient } from "@helpers/client/getSelectedCity";

import ColorModalCarousel from "../ColorModalCarousel";
import assetLoader from "@utils/internalImageLoder";
import CustomLink from "@components/atoms/Link";
import ChatbotSupport from "@components/molecules/ChatbotSupport";
import ImageResponsive from "@components/atoms/ImageResponsive";
import Image from "next/image";
import Text from "@components/atoms/Text";
import styles from "./ColorsSection.module.scss";
import Link from "next/link";

interface ColorsSectionClientWrapperProps {
  modelDetails: any;
  slug: string;
  hideExporeButton?: boolean;
  customTitle?: string;
  customDescription?: string;
  cityName?: string;
  carAvailability?: string;
  pageType?: string;
  isMM?: boolean;
  isInApp?: boolean;
}

export default function ColorsSectionClientWrapper({
  modelDetails,
  slug,
  hideExporeButton = false,
  customTitle,
  customDescription,
  cityName,
  carAvailability,
  pageType,
  isMM = false,
  isInApp
}: ColorsSectionClientWrapperProps) {
  const { name: clientCityName } = getSelectedCityClient();
  const carColors = modelDetails?.distinctColors;

  const modelName = modelDetails?.model?.name;
  const brandName = modelDetails?.brand?.name;

  const [selectedColor, setSelectedColor] = useState<
    (typeof carColors)[0] | null
  >(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const openModal = async (color: (typeof carColors)[0]) => {
    const modalImages = await colorMediaService.getColorMedia({
      slug: slug,
      colorId: color?.id,
      mediaTypes: "IMAGE",
      tagNames: "EXTERIOR",
    });
    if (modalImages.length > 0) {
      setSelectedColor({
        ...color,
        modalImages: modalImages.map((m: any) => m.url),
      });
    }
  };

  const closeModal = () => {
    setSelectedColor(null);
  };

  const handleColorCardClick = async (color: any) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || "",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_color",
        car_availability: carAvailability || "",
        vehicle_make: brandName || "",
        vehicle_model: modelName || "",
        vehicle_variant: "",
        city_name: clientCityName || "",
        colour: color?.name || "",
      },
    });
  };

  return (
    <>
      <div className={styles.desktopLayout}>
        <div className={styles.mainContent}>
          <div className={styles.colorsSection}>
            <div className={styles.scrollContainer} ref={scrollContainerRef}>
              <div className={styles.colorsGrid}>
                {carColors?.map((color: any, index: any) => (
                  <div key={index} className={styles.colorCard}>
                    {color?.isMultipleImagesAvailable && (
                      <div
                        className={styles.imageIconWrapper}
                        onClick={() => openModal(color)}
                      >
                        <Image
                          src={`${assetLoader()}/icons/image-popup.svg`}
                          alt={color.name}
                          fill
                          loading="lazy"
                          className={styles.imageIcon}
                        />
                      </div>
                    )}
                    <Link
                      href={`/cars/${slug}/colours/${toKebabCase(
                        color?.name
                      )}/`}
                      className={styles.imageContainer}
                      onClick={() => handleColorCardClick(color)}
                    >
                      <ImageResponsive
                        filePath={`${toKebabCase(brandName)}/${toKebabCase(
                          modelName
                        )}/${toKebabCase(color?.name.replace("+", "-plus"))}`}
                        imageTag={`Transparent`}
                        alt={color?.name}
                        width={170}
                        height={95}
                        className={styles.carImage}
                      />
                    </Link>
                    <Text level="p" className={styles.colorName}>{color.name}</Text>
                  </div>
                ))}
              </div>
            </div>
            {!hideExporeButton && (
              <CustomLink
                size="ds"
                className={styles.exploreBtn}
                href={`/cars/${slug}/colours/`}
                segmentEventProps={{
                  eventName: "action",
                  pageType: pageType,
                  eventProperties: {
                    action_type: "tap_btn",
                    action_name: "tap_btn_widget_cta",
                    cta_name: "Colours",
                    car_availability: carAvailability,
                    city_name: cityName,
                    vehicle_make: brandName,
                    vehicle_model: modelName,
                  },
                }}
              >
                {`Explore ${modelDetails?.model?.name} colours`}
              </CustomLink>
            )}
          </div>

          <ChatbotSupport
            heading="Want one that fits your budget?"
            subheading="We'll help you choose the perfect version for your needs."
            buttonText="Find my best match"
            buttonLink=""
            icon="/icons/chatbot.svg"
            customChatbotStyling={styles.customChatbotStyling}
            chatBotPrompt={`Which colour should I buy for ${brandName} ${modelName}?`}
            brandName={brandName}
            modelName={modelName}
            cityName={cityName}
            section="Right colour"
            carAvailability={carAvailability}
            pageType={pageType}
          />
        </div>
      </div>

      <div>
        <div
          className={`${styles.mobileLayout} ${
            isMM ? styles.mobileLayoutWithoutGap : ""
          }`}
        >
          <div className={styles.mobileColorsGrid}>
            {getImageRows(carColors)?.map((row, rowIndex) => (
              <div key={rowIndex} className={styles.mobileRow}>
                {row?.map((color: any, index) => (
                  <div
                    key={index}
                    className={styles.mobileColorCard}
                    onClick={() => {
                      openModal(color);
                      handleColorCardClick(color);
                    }}
                  >
                    {color?.isMultipleImagesAvailable && (
                      <div className={styles.imageIconWrapper}>
                        <Image
                          src={`${assetLoader()}/icons/image-popup.svg`}
                          alt={color.name}
                          fill
                          loading="lazy"
                          className={styles.imageIcon}
                        />
                      </div>
                    )}
                    <div className={styles.imageContainer}>
                      <ImageResponsive
                        filePath={`${toKebabCase(brandName)}/${toKebabCase(
                          modelName
                        )}/${toKebabCase(color?.name)}`}
                        imageTag={`Transparent`}
                        alt={color?.name}
                        width={170}
                        height={85}
                        className={styles.carImage}
                      />
                    </div>
                    <Text level="p" className={styles.colorName}>{color.name}</Text>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {!isInApp && !hideExporeButton && (
            <CustomLink
              size="ds"
              className={styles.exploreBtn}
              href={`/cars/${slug}/colours/`}
              segmentEventProps={{
                eventName: "action",
                pageType: pageType,
                eventProperties: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_widget_cta",
                  cta_name: "Colours",
                  car_availability: carAvailability,
                  city_name: cityName,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                },
              }}
            >
              {`Explore ${modelDetails?.model?.name} colours`}
            </CustomLink>
          )}

          {
            !isInApp &&
            <ChatbotSupport
              heading="Not sure which colour to pick?"
              subheading="We’ll help you find a colour you’ll love"
              buttonText="Help me choose"
              buttonLink=""
              icon="/icons/chatbot.svg"
              customChatbotStyling={styles.customChatbotStyling}
              chatBotPrompt={`Which colour should I buy for ${brandName} ${modelName}?`}
              brandName={brandName}
              modelName={modelName}
              cityName={cityName}
              section="Right colour"
              carAvailability={carAvailability}
              pageType={pageType}
            />
          }
        </div>
      </div>

      {selectedColor && (
        <ColorModalCarousel
          selectedColor={selectedColor}
          closeModal={closeModal}
          modelName={`${modelDetails?.brand?.name} ${modelDetails?.model?.name}`}
        />
      )}
    </>
  );
}
